import Card, { type CardProps } from '@creator/ui/components/Card/CardV2';
import Linkify from '@creator/ui/components/Linkify/Linkify';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import { convertFirebaseTimestampToDate, formatDate } from '@creator/ui/utils/date-utils';
import { cn } from '@creator/ui/utils/ui';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { hasSomeTokenPermission } from '@src/model/user/permission';
import { abbreviatedFormatter } from '@src/utils/number-utils/number-utils';
import { propsEqualityFn, storeEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export interface TokenPageAboutCardProps {
    enableMultiline?: boolean;
    tokenName: string;
    className?: string;
    cardProps?: CardProps;
}

const TokenPageAboutCard: FC<TokenPageAboutCardProps> = props => {
    const { tokenName, className } = props;

    const { t } = useTranslation();

    const navigate = useNavigate();
    const token = useStoreState(state => state.token.getToken(tokenName), storeEqualityFn);

    function clickRedirectelement(url: string): void {
        navigate(url);
    }

    function renderAbout() {
        if (!token) return <Spinner className="mx-auto mt-4 mb-4 " />;

        return (
            <Text data-test-id="channel-about-text" className="block text-sm whitespace-pre-line" enableMultiline={true}>
                <Linkify>{token.about}</Linkify>
            </Text>
        );
    }

    function renderBasicInfo() {
        if (!token) return null;
        const { creatorName, createdAt = '' } = token ?? {};

        return (
            <div className="mt-4">
                <div className="flex text-sm font-bold">
                    <Text className="flex-1">{t('aboutTokenCard.adminTitle')}</Text>
                    <Text className="">{t('aboutTokenCard.createdAtTitle')}</Text>
                </div>
                <div className="flex">
                    <div className="flex items-center flex-1 mt-2 cursor-pointer" onClick={(): void => clickRedirectelement(`/${ensureTokenUrl(tokenName)}/author/${creatorName}`)}>
                        <ProfileImage tokenName={tokenName} className="w-6 h-6 mr-2" displayName={creatorName} />
                        <UserDisplayName displayName={creatorName} textProps={{ className: 'link overflow-hidden text-sm font-bold' }} />
                    </div>
                    <Text className="block mt-3 text-sm">{formatDate(convertFirebaseTimestampToDate(createdAt))}</Text>
                </div>
            </div>
        );
    }

    function renderHolders() {
        if (!token) return null;
        const { numberOfMembers = 0 } = token ?? {};

        return (
            <div
                className="flex items-center pt-4 text-sm border-t border-gray-100 cursor-pointer"
                onClick={() => clickRedirectelement(`/${ensureTokenUrl(tokenName)}/tab/holders`)}
            >
                <Text className="flex-1 link">{t('aboutTokenCard.totalHolders')}</Text>
                <Text className="font-bold link">{abbreviatedFormatter(numberOfMembers)}</Text>
            </div>
        );
    }

    function renderTotalPosts() {
        if (!token) return null;
        const { totalPosts = 0 } = token ?? {};

        return (
            <div className="flex items-center text-sm">
                <Text className="flex-1">{t('aboutTokenCard.totalPosts')}</Text>
                <Text className="font-bold">{abbreviatedFormatter(totalPosts)}</Text>
            </div>
        );
    }

    function renderTitle() {
        if (!token) return null;
        return (
            <Text enableMultiline={true} className="text-base font-bold leading-none">
                {t('aboutTokenCard.title', { tokenName: token.title })}
            </Text>
        );
    }

    function renderTokenName() {
        if (!token) return null;
        if (!hasSomeTokenPermission(tokenName)) return null;

        return (
            <div className="flex items-center text-sm">
                <Text className="flex-1">{t('aboutTokenCard.tokenName')}</Text>
                <Text className="font-bold">{tokenName}</Text>
            </div>
        );
    }

    return (
        <Card data-test-id="about-card" className={cn('p-4 space-y-4', className)}>
            {renderTitle()}
            {renderAbout()}
            {renderBasicInfo()}
            {renderHolders()}
            {renderTotalPosts()}
            {renderTokenName()}
        </Card>
    );
};

export default memo(TokenPageAboutCard, propsEqualityFn);
